import * as React from 'react';
import {FormControl, FormHelperText, FormLabel, Box, TextField as TextFieldEl} from '@mui/material';
import { useMemo, memo } from 'react';

interface EmailProps {
    variant?: 'outlined' | 'filled' | 'standard';
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    label?: string;
    description?: string;
    required?: boolean;
    value: string;
    error?: string;
    enabled?: boolean;
    visible?: boolean;
    appValidated?: boolean;
}

const Email = memo((props: EmailProps) => {
    const {
        variant = 'outlined',
        onChange,
        label,
        description,
        required = false,
        value = '',
        error = '',
        enabled = true,
        visible = true,
        appValidated = false
    } = props;

    const [appHasBeenValidated, setAppHasBeenValidated] = React.useState(false);

    React.useEffect(() => {
        if (appValidated) {
            setAppHasBeenValidated(true);
        }
    }, [appValidated]);

    const showRequiredError = useMemo(() =>
            required && appHasBeenValidated && !value,
        [required, appHasBeenValidated, value]
    );

    const showError = useMemo(() =>
            error !== undefined && error !== "",
        [error, value]
    );

    const hasError = showError || showRequiredError;
    let errorMessage = showRequiredError ? 'This field is required' : error;
    if (error?.includes('must match pattern')) {
        errorMessage = 'Please ensure a valid email is entered in the required email format';
    }

    // Memoize computed values
    const showDescription = useMemo(() =>
            description !== undefined && description !== "",
        [description]
    );

    // Early return for hidden components
    if (!visible) {
        return null;
    }

    return (
        <Box sx={{ display: 'block' }}>
            <FormControl className="form-control" fullWidth={true}>
                <FormLabel required={required} color="secondary" className="form-separate-label">
                    {label}
                </FormLabel>
                <TextFieldEl
                    error={hasError}
                    required={required}
                    value={value}
                    onChange={onChange}
                    disabled={!enabled}
                    type='email'
                    variant={variant}
                    className='form-control-field'
                    sx={{ ".Mui-disabled": { opacity: 0.6 } }}
                    fullWidth
                />
                {showDescription && (
                    <FormHelperText>
                        {description}
                    </FormHelperText>
                )}
                {hasError && (
                    <FormHelperText error={true}>
                        {errorMessage}
                    </FormHelperText>
                )}
            </FormControl>
        </Box>
    );
});

Email.displayName = 'Email';

export default Email;
